
import { adapterRetailerMessage } from '@Adapters/cms/messages.adapter';
import { PLP_SEARCH } from '@Settings/listing.settings';
import { mapActions, mapState } from 'pinia';
import { useCommon } from '@Stores/common';
import { useSearch } from '@Stores/search';
import { useUser } from '@Stores/user';
import { getSearchRedirection } from '@Utils/catalog';
import errorHandler from '@Utils/error-handler';
import {
  componentPageDisplay,
  internalSearchResultDisplay,
  userVarsSearchPageList,
  userVarsSearchResults,
} from '@Utils/analytics.events';
import SeoDataMixin from '@Mixins/seo-data';

export default {
  name: 'SearchPage',
  mixins: [SeoDataMixin],
  async asyncData(context) {
    const { $pinia, $publicAPI, error, from, params, redirect } = context;
    let bubbleFetched = false;
    let hasResults, total, message;

    const searchTerm = params?.term;

    const config = {
      ...PLP_SEARCH,
      endpoint: {
        ...PLP_SEARCH.endpoint,
        query: {
          searchTerm,
        },
      },
    };

    try {
      const result = await $publicAPI.catalog.searchProducts({
        searchTerm,
        page: 1,
        size: 1,
      });

      const redirection = getSearchRedirection(
        result.redirect,
        searchTerm,
        context
      );

      total = result?.page?.totalElements;
      hasResults = total && total > 0;

      if (process.client) {
        internalSearchResultDisplay({
          hasResults,
          searchTerm,
        });
      }

      if (redirection || hasResults) {
        const userStore = useUser();
        userStore.addSearchTerm(searchTerm);
      }

      if (redirection) {
        if (process.client) {
          userVarsSearchResults({
            isRedirect: true,
            resultCount: 0,
            searchTerm,
          });

          context.$shoppingTools.zone = 'search';
          context.$shoppingTools.searchTerm = searchTerm;
          context.$shoppingTools.type = 'redirect';
        }

        if (from && from.path === redirection.path) {
          useCommon($pinia).setPageReload(true);
        }

        return redirect(302, redirection);
      }
    } catch (err) {
      errorHandler(err, context, {
        logOnly: true,
        path: 'pages.search.index.asyncData.searchProducts',
      });
      error(err);
    }

    try {
      const fetch = [];

      if (process.client) {
        fetch.push(
          $publicAPI.cms.getEntry('bubble_message', {
            params: {
              input: searchTerm,
            },
          })
        );
      }

      const results = await Promise.allSettled(fetch);
      results.forEach(({ status, reason, value }, index) => {
        if (status === 'rejected') {
          errorHandler(reason, context, {
            logOnly: true,
            path: 'pages.search.index.asyncData.rejected',
          });
          return;
        }

        if (!value) {
          return;
        }

        switch (index) {
          case 0:
            message = adapterRetailerMessage(value?.[0], context);
            break;
        }
      });

      if (process.client) {
        bubbleFetched = true;
      }
    } catch (err) {
      errorHandler(err, context, {
        logOnly: true,
        path: 'pages.search.index.asyncData.all',
      });
    }

    return {
      bubbleFetched,
      config,
      hasResults,
      message,
      searchTerm,
      total,
    };
  },
  data() {
    return {
      bubbleFetched: false,
      config: undefined,
      hasResults: false,
      message: undefined,
      searchTerm: undefined,
      total: undefined,
    };
  },
  fetch() {
    this.fetchCampaigns();
  },
  fetchOnServer: false,
  computed: {
    ...mapState(useUser, ['site']),
    listingKey() {
      return `listing-search-${this.site?.id}-${this.site?.mode}`;
    },
    seo() {
      return {
        robots: 'none',
        title: this.$t('meta.search.title', { searchTerm: this.searchTerm }),
      };
    },
  },
  watch: {
    site() {
      this.updateSearch();
    },
  },
  created() {
    if (process.client && !this.hasResults) {
      componentPageDisplay(this, {
        empty: !this.hasResults,
        searchTerm: this.searchTerm,
      });

      this.sendAnalytics();
    }
  },
  methods: {
    ...mapActions(useSearch, {
      showSearch: 'show',
    }),
    ...mapActions(useUser, ['addSearchTerm']),

    onLoaded(data) {
      if (process.client) {
        data = {
          ...data,
          empty: !this.hasResults,
          searchTerm: this.searchTerm,
        };

        this.$shoppingTools.sort = data.sort;
        this.sendAnalytics();
      }

      componentPageDisplay(this, data);
    },
    sendAnalytics() {
      userVarsSearchResults({
        isRedirect: false,
        resultCount: this.total,
        searchTerm: this.searchTerm,
      });

      userVarsSearchPageList(this.total);

      this.$shoppingTools.searchTerm = this.searchTerm;
      this.$shoppingTools.type = 'noRedirect';
    },
    async updateSearch() {
      try {
        const result = await this.$publicAPI.catalog.searchProducts({
          searchTerm: this.searchTerm,
          page: 1,
          size: 1,
        });

        const redirection = getSearchRedirection(
          result.redirect,
          this.searchTerm,
          this
        );

        const total = result?.page?.totalElements;
        this.hasResults = total && total > 0;

        if (redirection || this.hasResults) {
          const userStore = useUser();
          userStore.addSearchTerm(this.searchTerm);
        }

        if (redirection) {
          userVarsSearchResults({
            isRedirect: true,
            resultCount: 0,
            searchTerm: this.searchTerm,
          });

          this.$router.replace(redirection);
        }
      } catch (err) {
        errorHandler(err, this.$nuxt, {
          logOnly: true,
          path: 'pages.search.index.updateSearch',
        });
        this.$nuxt.error(err);
      }

      this.fetchCampaigns(true);
    },
    async fetchCampaigns(force) {
      if (this.bubbleFetched && !force) {
        return;
      }

      this.message = undefined;

      try {
        const fetch = [
          this.$publicAPI.cms.getEntry('bubble_message', {
            params: {
              input: this.searchTerm,
            },
          }),
        ];

        const results = await Promise.allSettled(fetch);
        results.forEach(({ status, reason, value }, index) => {
          if (status === 'rejected') {
            errorHandler(reason, this, {
              logOnly: true,
              path: 'pages.search.index.fetchCampaigns.rejected',
            });
            return;
          }

          if (!value) {
            return;
          }

          switch (index) {
            case 0:
              this.message = adapterRetailerMessage(value?.[0], this);
              break;
          }
        });

        this.bubbleFetched = true;
      } catch (err) {
        errorHandler(err, this, {
          logOnly: true,
          path: 'pages.search.index.fetchCampaigns.all',
        });
      }
    },
  },
};
